import type { FieldConfiguration } from "@/types/workflow"

// Helper to check if field is boolean
export function isBooleanField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'boolean' || field.customValuesType?.toLowerCase() === 'yesno';
}

// Helper to check if field is dropdown
export function isDropdownField(field: FieldConfiguration) {
  return (Array.isArray(field.customValues) && field.customValues.length > 0) || field.columnCode === 'AssignedToUser';
}

// Helper to check if field is textList
export function isTextListField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'textlist';
}

// Helper to check if field is date or datetime
export function isDateField(field: FieldConfiguration) {
  return field.columnTypeCode?.toLowerCase() === 'date' || field.columnTypeCode?.toLowerCase() === 'datetime' || field.customValuesType?.toLowerCase() === 'date';
}

// Helper to check if field is number, decimal, or money
export function isNumberField(field: FieldConfiguration) {
  const type = field.columnTypeCode?.toLowerCase();
  return type === 'number' || type === 'double' || type === 'decimal' || type === 'money';
}

// Helper to check if field is searchable
export function isSearchableField(field: FieldConfiguration) {
  return field.isSearchable === true;
}

// Helper to check if field is facetable
export function isFacetableField(field: FieldConfiguration) {
  return field.isFacetable === true;
}

// Helper to check if field is filterable
export function isFilterableField(field: FieldConfiguration) {
  return field.isFilterable === true;
}

// Helper to check if field has CodeValueSource
export function hasCodeValueSource(field: FieldConfiguration) {
  return !!field.codeValueSource;
}

// Helper to get the search column path (handles subColumnName)
export function getSearchColumnPath(column: FieldConfiguration | any): string {
  return column.subColumnName 
    ? `${column.columnCode}/${column.subColumnName}` 
    : column.columnCode;
}

// Helper to get facet keys from global filter configuration (with subColumnName paths)
export function getFacetKeysFromConfiguration(configuration: any): string[] {
  if (!configuration?.globalFilterIds || !configuration?.fieldsInUse) return [];
  
  return configuration.globalFilterIds
    .map((fid: string) => {
      const col = configuration.fieldsInUse.find((c: any) => c.fieldConfigurationId === fid);
      if (!col) return null;
      return getSearchColumnPath(col);
    })
    .filter((x: any): x is string => typeof x === 'string');
}

// Helper to get select fields from global filter configuration (just columnCode, no subColumnName)
export function getSelectFieldsFromConfiguration(configuration: any): string[] {
  if (!configuration?.globalFilterIds || !configuration?.fieldsInUse) return [];
  
  return configuration.globalFilterIds
    .map((fid: string) => {
      const col = configuration.fieldsInUse.find((c: any) => c.fieldConfigurationId === fid);
      if (!col) return null;
      return col.columnCode;
    })
    .filter((x: any): x is string => typeof x === 'string');
}

// Helper to get display value for columns with subColumnName (extracts property from objects)
export function getDisplayValueFromColumn(value: any, column: FieldConfiguration | any): string {
  // If column has subColumnName and value is an object, extract that property
  if (column.subColumnName && typeof value === 'object' && value !== null) {
    // Try different case variations of the property name
    const subColLower = column.subColumnName.toLowerCase();
    const foundKey = Object.keys(value).find(k => k.toLowerCase() === subColLower);
    if (foundKey && value[foundKey] !== undefined && value[foundKey] !== null) {
      return String(value[foundKey]);
    }
    // Fallback: try direct access
    if (value[column.subColumnName] !== undefined && value[column.subColumnName] !== null) {
      return String(value[column.subColumnName]);
    }
  }
  // For non-objects or when subColumnName is not set, return value as is
  return typeof value === 'object' ? JSON.stringify(value) : (value ?? "");
} 